import { useEffect } from "react";
import {
  useInfiniteQuery,
  useQuery,
  keepPreviousData,
} from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { TIME_INTERVALS } from "shared/lib/constants/time";
import { LIST_SUBSECTIONS_MAP } from "shared/lib/constants/lists";
import { ListSubsectionI } from "shared/lib/interfaces/lists";

import { useApiClient } from "@/context/api-client";
import {
  LISTS_QUERY_KEY,
  listWorkspaceQueryKeys,
} from "@/modules/list-workspace/queries/keys";

import {
  GetListViewRowsRequestParamsI,
  PullFilterOptionsI,
  SearchGlobalRequestI,
} from "@/api/routes/list";
import { useListsStore } from "@/modules/list-workspace/store/lists-store";
import { LIST_KEYS } from "@/modules/list-workspace/constants";
import { RESERVED_COLUMN_NAME_DIALS } from "@/modules/list-workspace/sections/table-section/constants";
import { glencocoClientAPI } from "@/api/glencoco";
import { retryOnTransportFailure } from "@/api/retry-on-transport-failure";

export const useWithStore_QueryFetchLists = () => {
  const api = useApiClient();

  const setIsListsAPIPending = useListsStore(
    (state) => state.setIsListsAPIPending
  );
  const setIsListsAPIFetching = useListsStore(
    (state) => state.setIsListsAPIFetching
  );
  const setLists = useListsStore((state) => state.setLists);

  const listsApi = useQuery({
    queryKey: listWorkspaceQueryKeys.lists(),
    retry: retryOnTransportFailure,
    networkMode: "always",
    refetchOnReconnect: true,
    queryFn: api.getLists,
    select: (data) => data.data.user_lists,
  });

  useEffect(() => {
    if (listsApi.isError) {
      toast.error("Failed to load lists. " + listsApi.error);
    }
  }, [listsApi.isError]);

  useEffect(() => {
    setIsListsAPIPending(listsApi.isPending);
  }, [listsApi.isPending]);

  useEffect(() => {
    setIsListsAPIFetching(listsApi.isFetching);
  }, [listsApi.isFetching]);

  useEffect(() => {
    if (listsApi.data) {
      setLists(listsApi.data);
    }
  }, [listsApi.data]);

  return listsApi;
};

export type FetchListsApiI = ReturnType<typeof useWithStore_QueryFetchLists>;

export const useWithStore_QueryListView = (listId?: string) => {
  const api = useApiClient();

  const setListViewId = useListsStore((state) => state.setListViewId);

  const listViewApi = useQuery({
    enabled: !!listId?.trim(),
    queryKey: listWorkspaceQueryKeys.listViews(listId as string),
    retry: retryOnTransportFailure,
    networkMode: "always",
    refetchOnReconnect: true,
    queryFn: () => api.getListViews(listId as string),
    select: (data) => data.data.views?.[0]?.view_id || null,
  });

  useEffect(() => {
    if (listViewApi.isError) {
      toast.error("Failed to load list views.");
    }
  }, [listViewApi.isError]);

  useEffect(() => {
    if (listViewApi.data) {
      setListViewId(listViewApi.data);
    }
  }, [listViewApi.data]);

  return listViewApi;
};

export const useWithStore_QueryViewMetadata = (listViewtId?: string) => {
  const api = useApiClient();

  const setFilters = useListsStore((state) => state.setFilters);
  const setSorts = useListsStore((state) => state.setSorts);
  const setColumns = useListsStore((state) => state.setColumns);
  const setMetadata = useListsStore((state) => state.setMetadata);
  const setIsMetadataAPIPending = useListsStore(
    (state) => state.setIsMetadataAPIPending
  );
  const setIsMetadataAPIFetching = useListsStore(
    (state) => state.setIsMetadataAPIFetching
  );

  const listViewMetadataApi = useQuery({
    enabled: !!listViewtId?.trim(),
    queryKey: listWorkspaceQueryKeys.view(listViewtId as string),
    retry: retryOnTransportFailure,
    networkMode: "always",
    refetchOnReconnect: true,
    queryFn: () => api.getListViewMetadata(listViewtId as string),
    select: (data) => data.data || null,
  });

  useEffect(() => {
    if (listViewMetadataApi.isError) {
      toast.error("Failed to load list view metadata.");
    }
  }, [listViewMetadataApi.isError]);

  useEffect(() => {
    if (listViewMetadataApi.data) {
      const data = listViewMetadataApi.data;

      const dialColumnTypeIds: string[] = [
        LIST_KEYS.DIALS,
        LIST_KEYS.NUMBER_OF_DIALS,
      ];

      setFilters(
        data?.filters?.filter(
          (f) =>
            !f.column_type_id || !dialColumnTypeIds.includes(f.column_type_id)
        )
      );
      setSorts(
        data?.sorts?.filter(
          (s) => !dialColumnTypeIds.includes(s.column_type_id)
        )
      );
      setColumns(
        data?.columns?.filter((c) => c.name !== RESERVED_COLUMN_NAME_DIALS)
      );

      setMetadata({
        campaigns: data?.campaigns,
        description: data?.description,
        is_default_list: data?.is_default_list,
        name: data?.name,
        number_available_contacts_to_call_cold:
          data?.number_available_contacts_to_call_cold,
        number_available_contacts_to_call_nurture:
          data?.number_available_contacts_to_call_nurture,
        number_cold_contacts: data?.number_cold_contacts,
        number_nurture_contacts: data?.number_nurture_contacts,
        number_total_contacts: data?.number_total_contacts,
        number_total_contacts_filtered: data?.number_total_contacts_filtered,
        number_cold_contacts_filtered: data?.number_cold_contacts_filtered,
        number_nurture_contacts_filtered:
          data?.number_nurture_contacts_filtered,
        number_available_contacts_to_call_cold_filtered:
          data?.number_available_contacts_to_call_cold_filtered,
        number_available_contacts_to_call_nurture_filtered:
          data?.number_available_contacts_to_call_nurture_filtered,
      });
    }
  }, [listViewMetadataApi.data]);

  useEffect(() => {
    setIsMetadataAPIPending(listViewMetadataApi.isPending);
  }, [listViewMetadataApi.isPending]);

  useEffect(() => {
    setIsMetadataAPIFetching(listViewMetadataApi.isFetching);
  }, [listViewMetadataApi.isFetching]);

  return listViewMetadataApi;
};

/**
 * The Nurture list polls for follow-ups that a disposition made outside this
 * tab, for example in Trellus (GLE-8460). React Query stops the interval
 * while the tab is hidden and refetches once when the tab becomes visible
 * again. Other lists and a running GC dialer keep their rows as they are,
 * because each poll reloads every loaded page of a slow endpoint.
 */
export const getViewRowsRefetchInterval = ({
  subsection,
  isDialerEnabled,
}: {
  subsection?: ListSubsectionI;
  isDialerEnabled?: boolean;
}): number | false => {
  if (subsection !== LIST_SUBSECTIONS_MAP.NURTURE || isDialerEnabled) {
    return false;
  }

  return TIME_INTERVALS.ONE_MINUTE;
};

export const useWithStore_QueryFetchViewRows = (
  listViewId?: string,
  params?: Partial<GetListViewRowsRequestParamsI>,
  { refetchInterval = false }: { refetchInterval?: number | false } = {}
) => {
  const api = useApiClient();

  const setRows = useListsStore((state) => state.setRows);
  const setIsRowsAPIPending = useListsStore(
    (state) => state.setIsRowsAPIPending
  );
  const setIsRowsAPIFetching = useListsStore(
    (state) => state.setIsRowsAPIFetching
  );
  // const setRowsNextToken = useListsStore((state) => state.setRowsNextToken);

  const listViewRowsApi = useInfiniteQuery({
    enabled: !!listViewId?.trim() && !!params,
    queryKey: listWorkspaceQueryKeys.rows(listViewId as string, params),
    retry: retryOnTransportFailure,
    networkMode: "always",
    refetchOnReconnect: true,
    refetchInterval,
    refetchIntervalInBackground: false,
    initialPageParam: undefined as string | null | undefined,
    queryFn: ({ pageParam }) =>
      api.getListViewRows(
        listViewId as string,
        {
          ...params,
          next_token: pageParam,
        } as GetListViewRowsRequestParamsI
      ),
    getNextPageParam: (lastPage) => lastPage.data?.next_token || undefined,

    select: (data) => {
      const rows = data?.pages
        .flatMap((page) => page.data.rows)
        .filter(Boolean);

      return rows?.length > 0 ? rows : [];
    },
  });

  useEffect(() => {
    if (listViewRowsApi.isError) {
      toast.error("Failed to load list rows.");
    }
  }, [listViewRowsApi.isError]);

  useEffect(() => {
    setIsRowsAPIPending(listViewRowsApi.isPending);
  }, [listViewRowsApi.isPending]);

  useEffect(() => {
    setIsRowsAPIFetching(listViewRowsApi.isFetching);

    // isRowsAPIPending is used to define the loading state of the table (triggered when filters or sorts are changed).
    // This is cleared here. If isFetching is false (and the API itself isn't actually pending), it means the data is loaded.
    if (!listViewRowsApi.isPending && !listViewRowsApi.isFetching) {
      setIsRowsAPIPending(false);
    }
  }, [listViewRowsApi.isFetching, listViewRowsApi.isPending]);

  useEffect(() => {
    setRows(listViewRowsApi.data || []);
    // setRowsNextToken(listViewRowsApi.data?.next_token || undefined);
  }, [listViewRowsApi.data]);

  return listViewRowsApi;
};

export const useWithStore_QueryFetchAvailableColumns = () => {
  const api = useApiClient();
  const setAvailableColumns = useListsStore(
    (state) => state.setAvailableColumns
  );

  const availableColumnsApi = useQuery({
    queryKey: listWorkspaceQueryKeys.availableColumns(),
    retry: retryOnTransportFailure,
    networkMode: "always",
    refetchOnReconnect: true,
    queryFn: api.getAvailableColumns,
    select: (data) => data.data.column_types,
  });

  useEffect(() => {
    if (availableColumnsApi.isError) {
      toast.error(
        "Failed to load available columns. " + availableColumnsApi.error
      );
    }
  }, [availableColumnsApi.isError]);

  useEffect(() => {
    if (availableColumnsApi.data) {
      setAvailableColumns(availableColumnsApi.data);
    }
  }, [availableColumnsApi.data]);

  return availableColumnsApi;
};

export const useSearchGlobal = (searchTerm: string) => {
  const api = useApiClient();

  const searchGlobalQuery = useQuery({
    enabled: !!searchTerm, // Only run the query if searchTerm is not empty
    queryKey: listWorkspaceQueryKeys.searchGlobal(searchTerm),
    retry: retryOnTransportFailure,
    networkMode: "always",
    refetchOnReconnect: true,
    // The previous term's results stay on screen while the next term loads.
    placeholderData: keepPreviousData,
    queryFn: () => {
      const params: SearchGlobalRequestI = { search_term: searchTerm };

      return api.searchGlobal(params);
    },
    select: (data) => data.data,
  });

  return searchGlobalQuery;
};

/**
 * Columns the view's owner can add: Industry and the declared custom fields
 * (GLE-8484). Only fetched while the column picker is open.
 */
export const useQueryAddableSourceColumns = (
  viewId: string | undefined,
  isEnabled: boolean
) => {
  const api = useApiClient();

  return useQuery({
    enabled: isEnabled && !!viewId?.trim(),
    queryKey: listWorkspaceQueryKeys.addableSourceColumns(viewId ?? ""),
    retry: retryOnTransportFailure,
    networkMode: "always",
    refetchOnReconnect: true,
    queryFn: () => api.getAddableSourceColumns(viewId as string),
    select: (data) => data.data.columns ?? [],
  });
};

/**
 * Fields and values the "Load 50 more accounts" pull of an exclusive list can
 * be filtered by, and whether the lead pool has leads left to pull. Fetched
 * while a load leads button is on screen.
 */
export const useQueryPullFilterOptions = (
  listId: string | undefined,
  isEnabled: boolean
) => {
  const api = useApiClient();

  return useQuery({
    enabled: isEnabled && !!listId?.trim(),
    queryKey: listWorkspaceQueryKeys.pullFilterOptions(listId ?? ""),
    retry: retryOnTransportFailure,
    networkMode: "always",
    refetchOnReconnect: true,
    // The backend refreshes these after ingestions and nightly; refetching
    // more often than this buys nothing.
    staleTime: TIME_INTERVALS.TEN_MINUTES,
    queryFn: () => api.getPullFilterOptions(listId as string),
    select: (data): PullFilterOptionsI => ({
      fields: data.data.fields ?? [],
      computedAt: data.data.computed_at ?? null,
      hasLeadsToPull: data.data.has_leads_to_pull ?? null,
    }),
  });
};

export const useFetchLists = () =>
  useQuery({
    queryKey: [LISTS_QUERY_KEY],
    retry: retryOnTransportFailure,
    networkMode: "always",
    refetchOnReconnect: true,
    queryFn: glencocoClientAPI().getLists,
  });
